// 剣舞 (アルレ版) の仮の FX を、描いた絵の質感へ替える比較版 (2026-10-31・10-31 作者の答えで配布版の既定へ・URL ?bladeFx=now で前の見た目)。
//   作者 (10-29 介錯): 線や輪を引いただけの見た目 =「コードで描いた感」が嫌。質感は描いた絵、出る・動く・消えるはコード。
//   記録 = docs/research/2026-10-31-blade-dancer-fx-v2.md。
//   型にした2技 (床に当たる所が「花びらと火花」= コードの線のままだった):
//     遅れの一撃 γ … 1発目の剣先から床にひびが走り、遅れて爆ぜる2発目は赤い炎が噴いて焦げ跡と煙が残る (赤＝弾けない技だけ)。
//     跳んで叩く δ … 突き立てた所に土ぼこりが左右へ広がり、2本の剣先の床が欠ける。払う2発目は前足の土ぼこり。
//   型に加えて広げた分 (10-31 夜・窓口「同じ方向で残りの技へ」。没になった時の無駄を小さくするため2つに留めた):
//     幕引きの一閃 (とどめ) … 床を滑り込む間、足もとから土ぼこりが後ろへ流れ、止まって斬る所で前へ土ぼこりが立つ。
//     崩れ … 片膝をつく所で、膝の下から土ぼこりが低く広がる。
//   絵は介錯の素材を借りる (public/fx/exec/burst_*・mats/)。炎の絵は紫なので、明るさだけ取り出して赤に塗り直す。
//   render.ts から呼ぶので render.ts を読み込まない (循環を作らない)。
import { phaseProgressOf } from './boss_pose_timeline'
import { burstSprite, BURST_ANCHOR_Y, BURST_FRAMES, drawAdditive, preloadExecFxSprites } from './exec_fx_sprites'
import { crackBitSprite, crackSprite, drawMat, dustSprite, preloadExecFxMats, scorchSprite, smokeSprite, tinted } from './exec_fx_mats'
import type { Boss } from './types'

const BOSS_ID = 'blade-dancer'
const GAMMA_ID = 'bladeCrimson'
const DELTA_ID = 'bladeDescent'
const EXEC_ID = 'dancerExecution'

// 配布版 = 'v2' (10-31 作者の答え: 遅れの一撃・跳んで叩く・崩れを描いた絵に。幕引きの一閃の土ぼこりは没
//   =「もうちょっとデザインを凝りたい」・技そのものを考え直すので床の絵だけ足さない)。
//   'now' = 前の花びらと火花・'v2exec' = 没にした幕引きの一閃の土ぼこりも出す (見比べ用)。
export type BladeFx = 'now' | 'v2' | 'v2exec'
let look: BladeFx = 'v2'
export function parseBladeFx(v: string | null): BladeFx { return v === 'now' ? 'now' : v === 'v2exec' ? 'v2exec' : 'v2' }
export function setBladeFx(v: BladeFx): void {
  look = v; fx.length = 0; last = null; lastX = null; kneeled = false
  if (v !== 'now') { preloadExecFxSprites(); preloadExecFxMats() }
}
export function bladeFxV2On(): boolean { return look !== 'now' }

// 床の絵1つ。x = 床の上の位置 (世界)・dir = ボスの向き・t0 = 出た時刻 (秒)。
type Kind = 'gammaCrack' | 'gammaBurst' | 'deltaLand' | 'deltaSweep' | 'glidePuff' | 'glideStop' | 'kneel'
type Fx = { kind: Kind; x: number; y: number; dir: 1 | -1; t0: number }
const fx: Fx[] = []
let last: { atk: string; state: string; hit: number } | null = null
let lastTime = -1
let lastX: number | null = null
let lastPuff = -1
let kneeled = false

const LIFE_S: Record<Kind, number> = { gammaCrack: 1.6, gammaBurst: 1.7, deltaLand: 1.1, deltaSweep: 0.6, glidePuff: 0.45, glideStop: 0.7, kneel: 0.9 }

// 当たる絵の剣先 (画面 px・足元から前)。γ = blade_dancer_gamma.ts の TIP_FWD_PX、δ = blade_dancer_delta.ts の d4 の2本。
const GAMMA_TIP = [152, 156] as const
const DELTA_TIPS = [83, 105] as const
const DELTA_SWEEP_FOOT = 70
// 崩れの入りの絵 (m1029_defeat_fall) から片膝をつく絵へ替わる所 (blade_dancer_arle.ts の BLADE_STAGGER_STEMS・撮影で 15 コマ目)。
const KNEEL_AT_S = 0.24
const GLIDE_PUFF_S = 0.05

/** 技の段が変わった所で床の絵を出す (毎フレーム・描く前)。 */
function spawn(boss: Boss, footY: number, time: number): void {
  const atk = boss.currentAttack?.id ?? ''
  const { phase, hitIndex } = phaseProgressOf(boss)
  const now = { atk, state: phase, hit: hitIndex }
  const entered = (k: number) => phase === 'active' && hitIndex === k
    && !(last && last.atk === atk && last.state === 'active' && last.hit === k)
  const dir: 1 | -1 = boss.facing === 'right' ? 1 : -1
  if (atk === GAMMA_ID) {
    if (entered(0)) fx.push({ kind: 'gammaCrack', x: boss.x + dir * GAMMA_TIP[0], y: footY, dir, t0: time })
    if (entered(1)) fx.push({ kind: 'gammaBurst', x: boss.x + dir * GAMMA_TIP[1], y: footY, dir, t0: time })
  } else if (atk === DELTA_ID) {
    if (entered(0)) fx.push({ kind: 'deltaLand', x: boss.x, y: footY, dir, t0: time })
    if (entered(1)) fx.push({ kind: 'deltaSweep', x: boss.x + dir * DELTA_SWEEP_FOOT, y: footY, dir, t0: time })
  } else if (atk === EXEC_ID && look === 'v2exec') {
    // 滑り込みは溜めの終わりにボスが床を動く間 (絵の名前でなく、動いたかで見る)。
    const moved = lastX !== null && Math.abs(boss.x - lastX) > 2
    if (phase === 'telegraph' && moved && time - lastPuff >= GLIDE_PUFF_S) {
      fx.push({ kind: 'glidePuff', x: boss.x, y: footY, dir, t0: time }); lastPuff = time
    }
    if (entered(0)) fx.push({ kind: 'glideStop', x: boss.x + dir * 30, y: footY, dir, t0: time })
  }
  if (boss.state === 'staggered') {
    if (!kneeled && boss.stateElapsed >= KNEEL_AT_S) { fx.push({ kind: 'kneel', x: boss.x, y: footY, dir, t0: time }); kneeled = true }
  } else kneeled = false
  lastX = boss.x
  last = now
}

/** 毎フレーム描く (ボスの絵の後)。比較版でない時・剣舞でない時は何もしない。 */
export function drawBladeFxV2(ctx: CanvasRenderingContext2D, boss: Boss | null | undefined, footY: number, time: number): void {
  if (look === 'now' || !boss || boss.id !== BOSS_ID) return
  if (time < lastTime) { fx.length = 0; last = null }
  lastTime = time
  spawn(boss, footY, time)
  for (let i = fx.length - 1; i >= 0; i--) if (time - fx[i]!.t0 > LIFE_S[fx[i]!.kind]) fx.splice(i, 1)
  for (const f of fx) {
    const t = time - f.t0
    if (f.kind === 'gammaCrack') drawGammaCrack(ctx, f, t)
    else if (f.kind === 'gammaBurst') drawGammaBurst(ctx, f, t)
    else if (f.kind === 'deltaLand') drawDeltaLand(ctx, f, t)
    else if (f.kind === 'deltaSweep') drawDeltaSweep(ctx, f, t)
    else if (f.kind === 'glidePuff') drawGlidePuff(ctx, f, t)
    else if (f.kind === 'glideStop') drawGlideStop(ctx, f, t)
    else drawKneel(ctx, f, t)
  }
}

const clamp01 = (v: number) => Math.max(0, Math.min(1, v))
const fadeOut = (t: number, from: number, to: number) => 1 - clamp01((t - from) / (to - from))

// γ 1発目: 剣先から前後へひびが走る (0.25 秒で伸びきる)。2発目が爆ぜた後も焦げ跡の下に残って薄れる。
//   ひびの絵は左端から右へ伸びるので、剣先の少し手前から前へ置く (ボスの向きで返す)。
function drawGammaCrack(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const img = crackSprite(Math.floor(clamp01(t / 0.25) * 3.999))
  if (!img) return
  const w = 150
  const h = w * img.naturalHeight / img.naturalWidth
  drawMat(ctx, img, f.x - f.dir * 70, f.y + 2, w, h, 0, 0.6, 0.95 * fadeOut(t, 0.9, 1.6), 0, f.dir < 0)
}

// γ 2発目: 赤く塗り直した炎が床から噴いて這い、ちろちろ残る (4段を 0.6 秒)。焦げ跡が育ち、煙が2筋のぼる。
function drawGammaBurst(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const sc = scorchSprite(t < 0.08 ? 0 : t < 0.18 ? 1 : t < 0.5 ? 2 : 3)
  if (sc) {
    const w = 120
    drawMat(ctx, sc, f.x, f.y + 6, w, w * sc.naturalHeight / sc.naturalWidth, 0.5, 0.86, fadeOut(t, 1.0, 1.7))
  }
  const dust = dustSprite('land', Math.floor(clamp01(t / 0.5) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, '92,62,58'), f.x, f.y + 4, 170, 120, 0.5, 1, 0.5 * fadeOut(t, 0.25, 0.7))
  const step = Math.floor(t / 0.15)
  if (step < BURST_FRAMES) {
    const img = burstSprite(step)
    if (img) {
      const k = (t - step * 0.15) / 0.15
      const w = 150 * (step === 0 ? 0.85 + 0.15 * k : 1)
      drawAdditive(ctx, redFlame(img), f.x, f.y + 4, w, w * img.naturalHeight / img.naturalWidth, 0.5, BURST_ANCHOR_Y,
        step === BURST_FRAMES - 1 ? 1 - k : 1, 0, f.dir < 0)
    }
  }
  for (let i = 0; i < 2; i++) {
    const s = smokeSprite(i + 1)
    const ts = t - 0.2 - i * 0.12
    if (!s || ts <= 0) continue
    const rise = ts * 26
    drawMat(ctx, s, f.x + (i ? 14 : -18) * f.dir, f.y - 4 - rise, 46, 46 * s.naturalHeight / s.naturalWidth, 0.5, 1,
      0.75 * clamp01(ts / 0.2) * fadeOut(ts, 0.6, 1.3))
  }
}

// δ 1発目: 落ちて突き立てた所から土ぼこりが左右へ低く広がり (4コマを 0.5 秒)、2本の剣先の床が欠けて欠片が跳ねる。
//   δ は弾ける技なので、ひびと欠片は赤くせず床板の焦げ茶に塗る (赤＝弾けない技だけ)。
//   土ぼこりは w260 だと主人公の体まで覆って見えなくなった (撮影 10-31) ので、足もとの高さに収める。
const DUST_GOLD = '206,186,150'
const FLOOR_DARK = '52,36,26'
function drawDeltaLand(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const mid = (DELTA_TIPS[0] + DELTA_TIPS[1]) / 2
  const crack = crackSprite(Math.floor(clamp01(t / 0.12) * 3.999))
  if (crack) {
    const w = 96
    drawMat(ctx, tinted(crack, FLOOR_DARK), f.x + f.dir * (mid - 48), f.y + 2, w, w * crack.naturalHeight / crack.naturalWidth, 0, 0.6,
      0.7 * fadeOut(t, 0.6, 1.1), 0, f.dir < 0)
  }
  const dust = dustSprite('land', Math.floor(clamp01(t / 0.5) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, DUST_GOLD), f.x + f.dir * 60, f.y + 6, 190, 120, 0.5, 1, 0.45 * fadeOut(t, 0.25, 0.75))
  DELTA_TIPS.forEach((tip, i) => {
    const bit = crackBitSprite(i * 2)
    if (!bit) return
    const tb = t - 0.02
    if (tb <= 0) return
    // 欠片は剣先から斜め上へ跳ねて落ちる。
    const vx = (i ? 70 : -40) * f.dir, vy = -150
    const x = f.x + f.dir * tip + vx * tb
    const y = f.y - 4 + vy * tb + 420 * tb * tb
    if (y > f.y + 2) return
    drawMat(ctx, tinted(bit, FLOOR_DARK), x, y, 16, 16, 0.5, 0.5, fadeOut(t, 0.35, 0.5), tb * 9 * (i ? 1 : -1))
  })
}

// δ 2発目: 低く踏み込んで払う前足から、土ぼこりが後ろへ蹴り上がる。
function drawDeltaSweep(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const dust = dustSprite('kick', Math.floor(clamp01(t / 0.4) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, DUST_GOLD), f.x, f.y + 4, 110, 90, 0.5, 1, 0.45 * fadeOut(t, 0.2, 0.6), 0, f.dir > 0)
}

// 広げた分の土ぼこりは、スポットライトの当たる明るい床の上に立つので、δ の金より淡く濃くしないと見えない (撮影 10-31 夜)。
const DUST_PALE = '238,228,206'

// 幕引きの一閃の滑り込み: 足もとから小さな土ぼこりが後ろへ蹴られて薄れる (0.05 秒ごとに1つ)。
//   体の紫の光と斬りの稲妻はそのまま。足すのは床の反応だけ (紫＝致命の印を増やさない)。
function drawGlidePuff(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const dust = dustSprite('kick', Math.floor(clamp01(t / 0.35) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, DUST_PALE), f.x - f.dir * 24, f.y + 4, 104, 74, 0.5, 1, 0.6 * fadeOut(t, 0.15, 0.45), 0, f.dir > 0)
}

// 幕引きの一閃の止まり: 滑ってきた勢いで、前へ土ぼこりが低く押し出される。
function drawGlideStop(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const dust = dustSprite('land', Math.floor(clamp01(t / 0.45) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, DUST_PALE), f.x, f.y + 6, 170, 100, 0.5, 1, 0.65 * fadeOut(t, 0.25, 0.7))
}

// 崩れ: 片膝が床に落ちた所から、土ぼこりが低く左右へ広がる。
function drawKneel(ctx: CanvasRenderingContext2D, f: Fx, t: number): void {
  const dust = dustSprite('land', Math.floor(clamp01(t / 0.5) * 3.999))
  if (dust) drawMat(ctx, tinted(dust, DUST_PALE), f.x, f.y + 6, 210, 116, 0.5, 1, 0.65 * fadeOut(t, 0.3, 0.9))
}

// 介錯の紫の炎の絵から明るさだけを取り、暗い赤 → 赤 → 芯の淡い桃へ塗り直した写し (絵ごとに1回だけ作る)。
const redCache = new WeakMap<HTMLImageElement, CanvasImageSource>()
function redFlame(img: HTMLImageElement): HTMLImageElement {
  const hit = redCache.get(img)
  if (hit) return hit as HTMLImageElement
  if (typeof document === 'undefined') return img
  const c = document.createElement('canvas')
  c.width = img.naturalWidth; c.height = img.naturalHeight
  const g = c.getContext('2d')
  if (!g) return img
  g.drawImage(img, 0, 0)
  const d = g.getImageData(0, 0, c.width, c.height)
  const p = d.data
  for (let i = 0; i < p.length; i += 4) {
    const l = Math.max(p[i]!, p[i + 1]!, p[i + 2]!) / 255
    p[i] = 255 * Math.pow(l, 0.75)
    p[i + 1] = 235 * Math.pow(clamp01((l - 0.55) / 0.45), 1.6)
    p[i + 2] = 215 * Math.pow(clamp01((l - 0.65) / 0.35), 1.6)
  }
  g.putImageData(d, 0, 0)
  redCache.set(img, c)
  // drawAdditive は HTMLImageElement を受けるが、drawImage には canvas も渡せる。
  return c as unknown as HTMLImageElement
}

/** 比較版で外す、コードで描いていた床の花びらと火花の絵の名前 (γ の爆ぜ・δ の突き立て)。 */
export const BLADE_FX_V2_DROPPED_SMEARS = ['m1025_g_4', 'm1025_d_4'] as const
